"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { siteConfig } from "@/config/site";
import { navLinks } from "@/config/navigation";
import { currentPhase, getCTAConfig } from "@/config/game-status";
import { useTranslation } from "@/i18n";
import { LanguageSwitcher } from "@/components/ui/LanguageSwitcher";
import { cn } from "@/lib/utils";

export function Header() {
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const { t } = useTranslation();
    const cta = getCTAConfig(currentPhase, siteConfig.steamUrl);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 50);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            className={cn(
                "fixed top-0 left-0 right-0 z-50 transition-all duration-500",
                scrolled
                    ? "bg-void/80 backdrop-blur-xl border-b border-border/50"
                    : "bg-transparent"
            )}
        >
            <nav className="mx-auto max-w-6xl flex items-center justify-between px-6 md:px-8 h-16" aria-label="Main navigation">
                {/* Logo / Studio Name */}
                <a href="/" className="flex items-center gap-2 group">
                    <img
                        src="/favicon.webp"
                        alt=""
                        width={32}
                        height={32}
                        className="rounded"
                        aria-hidden="true"
                    />
                    <span className="text-sm font-bold tracking-wider text-text group-hover:text-mars-light transition-colors">
                        {siteConfig.studioName.toUpperCase()}
                    </span>
                </a>

                {/* Desktop nav */}
                <div className="hidden md:flex items-center gap-8">
                    {navLinks.map((link) => (
                        <a
                            key={link.href}
                            href={link.href}
                            className="text-sm text-text-secondary hover:text-text transition-colors"
                        >
                            {t.nav[link.label.toLowerCase() as keyof typeof t.nav] ?? link.label}
                        </a>
                    ))}
                    <a
                        href={cta.primary.href}
                        target={cta.primary.external ? "_blank" : undefined}
                        rel={cta.primary.external ? "noopener noreferrer" : undefined}
                        className="text-sm font-medium text-mars hover:text-mars-light transition-colors"
                    >
                        {t.cta.wishlist}
                    </a>
                    <LanguageSwitcher />
                </div>

                {/* Mobile toggle */}
                <button
                    onClick={() => setMobileOpen(!mobileOpen)}
                    className="md:hidden flex flex-col gap-1.5 p-2 cursor-pointer"
                    aria-label={mobileOpen ? "Close menu" : "Open menu"}
                    aria-expanded={mobileOpen}
                    aria-controls="mobile-menu"
                >
                    <span
                        className={cn(
                            "w-5 h-0.5 bg-text transition-all duration-300",
                            mobileOpen && "rotate-45 translate-y-[4px]"
                        )}
                    />
                    <span
                        className={cn(
                            "w-5 h-0.5 bg-text transition-all duration-300",
                            mobileOpen && "-rotate-45 -translate-y-[4px]"
                        )}
                    />
                </button>
            </nav>

            {/* Mobile menu */}
            <AnimatePresence>
                {mobileOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                        className="md:hidden bg-void/95 backdrop-blur-xl border-b border-border overflow-hidden"
                        id="mobile-menu"
                    >
                        <div className="flex flex-col gap-4 px-6 py-6">
                            {navLinks.map((link) => (
                                <a
                                    key={link.href}
                                    href={link.href}
                                    onClick={() => setMobileOpen(false)}
                                    className="text-base text-text-secondary hover:text-text transition-colors"
                                >
                                    {t.nav[link.label.toLowerCase() as keyof typeof t.nav] ?? link.label}
                                </a>
                            ))}
                            <a
                                href={cta.primary.href}
                                target={cta.primary.external ? "_blank" : undefined}
                                rel={cta.primary.external ? "noopener noreferrer" : undefined}
                                className="text-base font-medium text-mars hover:text-mars-light transition-colors"
                            >
                                {t.cta.wishlist}
                            </a>
                            <LanguageSwitcher className="mt-1" />
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
